import { useState, useEffect, useMemo } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { Separator } from "@/components/ui/separator";
import { ArrowLeft, User, FileText, TrendingUp, TrendingDown, ScrollText } from "lucide-react";
import { getCustomerById, getBillsByCustomer, getCustomerBalance } from "@/lib/api";
import { exportCSV, exportPDF } from "@/lib/exportUtils";

export default function CustomerLedger() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [customer, setCustomer] = useState(null);
  const [bills, setBills] = useState([]);
  const [balance, setBalance] = useState({ totalBilled: 0, totalPaid: 0, advanceBalance: 0, outstanding: 0 });

  useEffect(() => {
    getCustomerById(id).then(setCustomer);
    getBillsByCustomer(id).then(setBills);
    getCustomerBalance(id).then(setBalance).catch(() => {});
  }, [id]);

  const sortedBills = useMemo(
    () => [...bills].sort((a, b) => new Date(a.billDate) - new Date(b.billDate)),
    [bills]
  );

  const ledgerEntries = useMemo(() => {
    let runBal = 0;
    return sortedBills.map((bill) => {
      const billed = bill.netPayable || 0;
      const paid = bill.totalPaid || (bill.isPaid ? billed : 0);
      runBal += billed - paid;
      const status = bill.status || (bill.isPaid ? "paid" : paid > 0 ? "partial" : "unpaid");
      return { ...bill, paidAmount: paid, runningBalance: runBal, displayStatus: status };
    });
  }, [sortedBills]);

  const columns = [
    { label: "Date", accessor: (r) => new Date(r.billDate).toLocaleDateString("en-IN") },
    { label: "Invoice No", accessor: (r) => r.invoiceNo },
    { label: "Units", accessor: (r) => (r.consumedUnits || 0).toFixed(2) },
    { label: "Billed", accessor: (r) => (r.netPayable || 0).toFixed(2) },
    { label: "Paid", accessor: (r) => (r.paidAmount || 0).toFixed(2) },
    { label: "Status", accessor: (r) => r.displayStatus },
    { label: "Balance", accessor: (r) => r.runningBalance?.toFixed(2) },
  ];

  const statusBadge = (s) => {
    if (s === "paid") return <Badge className="bg-emerald-100 text-emerald-700 text-[10px]">Paid</Badge>;
    if (s === "partial") return <Badge className="bg-amber-100 text-amber-700 text-[10px]">Partial</Badge>;
    return <Badge variant="destructive" className="text-[10px]">Unpaid</Badge>;
  };

  if (!customer) {
    return (
      <div className="p-8 text-center text-zinc-400" data-testid="ledger-not-found">
        <p>Customer not found.</p>
        <Button onClick={() => navigate("/customers")} variant="link" className="mt-4">Go Back</Button>
      </div>
    );
  }

  return (
    <div className="p-4 md:p-8 lg:p-12 max-w-[1600px] mx-auto" data-testid="customer-ledger-page">
      <div className="flex flex-wrap items-center gap-4 mb-8">
        <Button data-testid="ledger-back-button" variant="outline" onClick={() => navigate("/customers")} className="rounded-sm border-zinc-300">
          <ArrowLeft className="w-4 h-4 mr-2" /> Back
        </Button>
        <h1 className="font-heading text-3xl sm:text-4xl tracking-tight font-black text-zinc-900">Payment Ledger</h1>
        <Button data-testid="monthly-statement-btn" variant="outline" onClick={() => navigate(`/statement?mode=customer&cid=${id}`)} className="rounded-sm border-zinc-300 ml-auto">
          <ScrollText className="w-4 h-4 mr-2" /> Monthly Statement
        </Button>
      </div>

      {/* Customer Info */}
      <Card className="border border-zinc-200 rounded-none md:rounded-sm shadow-none mb-6">
        <CardContent className="p-6">
          <div className="flex flex-wrap gap-6 items-center">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-orange-50 rounded flex items-center justify-center"><User className="w-5 h-5 text-orange-600" /></div>
              <div>
                <p className="font-heading font-bold text-lg">{customer.name}</p>
                <p className="text-xs font-mono text-zinc-500">{customer.consumerNumber}</p>
              </div>
            </div>
            <Separator orientation="vertical" className="h-12 hidden sm:block" />
            <div className="text-sm space-y-1">
              <p><span className="text-zinc-500">Society:</span> {customer.society || "—"}</p>
              <p><span className="text-zinc-500">Flat/Plot:</span> {customer.flatNo || customer.plotNo || "—"}</p>
            </div>
            <Separator orientation="vertical" className="h-12 hidden sm:block" />
            <div className="text-sm space-y-1">
              <p><span className="text-zinc-500">Phone:</span> {customer.phone || "—"}</p>
              <p><span className="text-zinc-500">Type:</span> {customer.connectionType}</p>
            </div>
            <Separator orientation="vertical" className="h-12 hidden sm:block" />
            <div className="text-sm space-y-1">
              <p><span className="text-zinc-500">Initial Reading:</span> <span className="font-mono font-semibold">{(customer.initialReading || 0).toFixed(2)}</span></p>
              <p><span className="text-zinc-500">Last Reading:</span> <span className="font-mono font-semibold">{(customer.lastReading || 0).toFixed(2)}</span></p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* 4 Summary Cards */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-6">
        <Card className="border border-zinc-200 rounded-none md:rounded-sm shadow-none">
          <CardContent className="p-5">
            <p className="text-[10px] font-bold uppercase tracking-[0.15em] text-zinc-500 mb-1">Total Billed</p>
            <p className="text-xl font-heading font-black font-mono" data-testid="ledger-total-billed">{"\u20B9"}{balance.totalBilled.toFixed(2)}</p>
          </CardContent>
        </Card>
        <Card className="border border-zinc-200 rounded-none md:rounded-sm shadow-none">
          <CardContent className="p-5">
            <p className="text-[10px] font-bold uppercase tracking-[0.15em] text-emerald-600 mb-1">Total Paid</p>
            <p className="text-xl font-heading font-black font-mono text-emerald-600" data-testid="ledger-total-paid">{"\u20B9"}{balance.totalPaid.toFixed(2)}</p>
          </CardContent>
        </Card>
        <Card className="border border-zinc-200 rounded-none md:rounded-sm shadow-none">
          <CardContent className="p-5">
            <p className="text-[10px] font-bold uppercase tracking-[0.15em] text-emerald-600 mb-1">
              <TrendingUp className="w-3 h-3 inline mr-1" />Advance Balance
            </p>
            <p className="text-xl font-heading font-black font-mono text-emerald-600" data-testid="ledger-advance">{"\u20B9"}{balance.advanceBalance.toFixed(2)}</p>
          </CardContent>
        </Card>
        <Card className="border border-zinc-200 rounded-none md:rounded-sm shadow-none">
          <CardContent className="p-5">
            <p className="text-[10px] font-bold uppercase tracking-[0.15em] text-red-600 mb-1">
              <TrendingDown className="w-3 h-3 inline mr-1" />Outstanding
            </p>
            <p className="text-xl font-heading font-black font-mono text-red-600" data-testid="ledger-outstanding">{"\u20B9"}{balance.outstanding.toFixed(2)}</p>
          </CardContent>
        </Card>
      </div>

      {/* Ledger Table */}
      <Card className="border border-zinc-200 rounded-none md:rounded-sm shadow-none">
        <CardHeader className="flex flex-row items-center justify-between flex-wrap gap-2">
          <CardTitle className="text-xs font-bold uppercase tracking-[0.15em] text-zinc-500">Transaction History</CardTitle>
          <div className="flex gap-2">
            <Button data-testid="ledger-export-pdf" variant="outline" size="sm" className="rounded-sm text-xs"
              onClick={() => exportPDF(`Ledger - ${customer.name}`, columns, ledgerEntries, `ledger_${customer.consumerNumber}`)}>PDF</Button>
            <Button data-testid="ledger-export-csv" variant="outline" size="sm" className="rounded-sm text-xs"
              onClick={() => exportCSV(ledgerEntries, columns, `ledger_${customer.consumerNumber}`)}>CSV</Button>
          </div>
        </CardHeader>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow className="bg-zinc-50">
                  <TableHead className="text-xs font-bold uppercase text-zinc-500">Date</TableHead>
                  <TableHead className="text-xs font-bold uppercase text-zinc-500">Invoice</TableHead>
                  <TableHead className="text-xs font-bold uppercase text-zinc-500">Units</TableHead>
                  <TableHead className="text-xs font-bold uppercase text-zinc-500 text-right">Billed</TableHead>
                  <TableHead className="text-xs font-bold uppercase text-zinc-500 text-right">Paid</TableHead>
                  <TableHead className="text-xs font-bold uppercase text-zinc-500 text-right">Balance</TableHead>
                  <TableHead className="text-xs font-bold uppercase text-zinc-500">Status</TableHead>
                  <TableHead className="text-xs font-bold uppercase text-zinc-500 text-right">View</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {ledgerEntries.length === 0 ? (
                  <TableRow><TableCell colSpan={8} className="text-center text-sm text-zinc-400 py-12">No billing history.</TableCell></TableRow>
                ) : ledgerEntries.map((entry) => (
                  <TableRow key={entry.id} data-testid={`ledger-row-${entry.id}`} className="hover:bg-zinc-50">
                    <TableCell className="text-sm">{new Date(entry.billDate).toLocaleDateString("en-IN")}</TableCell>
                    <TableCell className="text-sm font-mono">{entry.invoiceNo}</TableCell>
                    <TableCell className="text-sm font-mono">{(entry.consumedUnits || 0).toFixed(2)}</TableCell>
                    <TableCell className="text-sm font-mono text-right">{"\u20B9"}{(entry.netPayable || 0).toFixed(2)}</TableCell>
                    <TableCell className="text-sm font-mono text-right text-emerald-600">{"\u20B9"}{(entry.paidAmount || 0).toFixed(2)}</TableCell>
                    <TableCell className={`text-sm font-mono text-right font-semibold ${entry.runningBalance > 0 ? "text-red-600" : entry.runningBalance < 0 ? "text-emerald-600" : ""}`}>
                      {entry.runningBalance > 0 ? `\u20B9${entry.runningBalance.toFixed(2)}` : entry.runningBalance < 0 ? `Adv \u20B9${Math.abs(entry.runningBalance).toFixed(2)}` : "—"}
                    </TableCell>
                    <TableCell>{statusBadge(entry.displayStatus)}</TableCell>
                    <TableCell className="text-right">
                      <Button data-testid={`ledger-view-${entry.id}`} variant="ghost" size="sm" onClick={() => navigate(`/bills/invoice/${entry.id}`)}>
                        <FileText className="w-4 h-4" />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
